// Media helpers for HIMT: webcam/mic/screen streams + VAD + recorder.

export async function getCamMicStream() {
  // Low-resolution capture — the webcam recording is proctoring evidence
  // only, not HD footage. 640×360 @ 15fps with ~250 kbps video produces
  // ~2 MB per minute, survives flaky shipboard Wi-Fi, and still shows
  // the candidate's face clearly enough to verify identity. Previously
  // we asked for 1280×720 @ default bitrate (~2.5 Mbps) which generated
  // ~20 MB/min and swamped the chunk uploader on weak networks — only
  // the last ~30s of recording would make it to the server before the
  // test ended.
  return navigator.mediaDevices.getUserMedia({
    video: {
      width: { ideal: 640, max: 854 },
      height: { ideal: 360, max: 480 },
      frameRate: { ideal: 15, max: 20 },
      facingMode: "user",
    },
    audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 },
  });
}

export async function getScreenStream() {
  // IMPORTANT: use the minimal, maximally-compatible getDisplayMedia call.
  //
  // Why: Previously we passed `displaySurface: "monitor"` + experimental
  // Chrome-only hints (`monitorTypeSurfaces`, `selfBrowserSurface`,
  // `surfaceSwitching`, `systemAudio`). On macOS Chrome these ARE observed
  // to keep the native picker's "Share" button permanently greyed-out when
  // the candidate's display config is anything non-trivial — Sidecar iPad,
  // AirPlay receiver, mirrored external display, or secondary monitor.
  // The picker renders, the preview shows, but Chrome can't satisfy the
  // hard constraint + refuses to enable Share. We saw this exact failure
  // with a real candidate (Kirtan S Vakil, Apr 2026 WhatsApp report).
  //
  // The post-check below still enforces the "entire screen" rule so
  // candidates can't cheat by sharing only a tab or single window.
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: { ideal: 10 } },
    audio: false,
  });
  const track = stream.getVideoTracks()[0];
  const settings = (track && track.getSettings && track.getSettings()) || {};
  const surface = settings.displaySurface;
  // Only reject when we have POSITIVE evidence the candidate picked a tab
  // or a single window. Some Chromium builds and Firefox return `undefined`
  // or `"screen"` even for full-monitor picks.
  if (surface === "browser" || surface === "window") {
    stream.getTracks().forEach((t) => t.stop());
    const err = new Error("SCREEN_NOT_MONITOR");
    err.name = "ScreenSurfaceError";
    err.surface = surface;
    throw err;
  }
  return stream;
}

export function supportedMime() {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "audio/webm;codecs=opus",
    "audio/webm",
  ];
  for (const m of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

export function pickMime(prefVideo = true) {
  const list = prefVideo
    ? ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"]
    : ["audio/webm;codecs=opus", "audio/webm"];
  for (const m of list) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

export class ChunkedRecorder {
  constructor(stream, mime, { onChunk, timesliceMs = 5000, videoBitsPerSecond } = {}) {
    this.stream = stream;
    this.mime = mime || "";
    this.chunks = [];
    this.recorder = null;
    this.onChunk = onChunk || null; // fired as each timeslice chunk arrives
    this.timesliceMs = timesliceMs;
    this.videoBitsPerSecond = videoBitsPerSecond || null;
  }
  start() {
    const opts = {};
    if (this.mime) opts.mimeType = this.mime;
    if (this.videoBitsPerSecond) opts.videoBitsPerSecond = this.videoBitsPerSecond;
    this.recorder = new MediaRecorder(this.stream, Object.keys(opts).length ? opts : undefined);
    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        this.chunks.push(e.data);
        if (this.onChunk) {
          // Fire-and-forget — the uploader deals with errors + retries.
          try { this.onChunk(e.data); } catch (err) { /* noop */ }
        }
      }
    };
    // timeslice=0 → monolithic recording: ondataavailable only fires on
    // stop() so the accumulated blob is a single playable webm file.
    // timeslice>0 → emit a blob every N ms (legacy chunked flow).
    if (this.timesliceMs && this.timesliceMs > 0) {
      this.recorder.start(this.timesliceMs);
    } else {
      this.recorder.start();
    }
  }
  async stop() {
    return new Promise((resolve) => {
      if (!this.recorder) return resolve(null);
      this.recorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: this.mime || "video/webm" });
        resolve(blob);
      };
      if (this.recorder.state !== "inactive") this.recorder.stop();
      else resolve(new Blob(this.chunks, { type: this.mime || "video/webm" }));
    });
  }
}

/**
 * Serial chunk uploader. Each webcam/screen MediaRecorder chunk is pushed
 * onto an internal queue and POSTed one at a time to /recording-chunk with
 * an auto-incrementing index. If the network is slow we backpressure (chunks
 * queue up) instead of racing uploads, which would corrupt chunk ordering.
 *
 *   const uploader = makeChunkUploader({ api, attemptId, kind: "webcam" });
 *   recorder = new ChunkedRecorder(stream, mime, { onChunk: uploader.push });
 *   recorder.start();
 *   // ... later
 *   await uploader.drain();
 *   await api.post(`/student/attempt/${attemptId}/recording-finalize`,
 *                  new URLSearchParams({ kind: "webcam" }));
 */
export function makeChunkUploader({ api, attemptId, kind, onQueueChange }) {
  let nextIdx = 0;
  let queue = [];
  let active = false;
  const notify = () => { try { onQueueChange && onQueueChange(queue.length); } catch (_e) {} };

  const flush = async () => {
    if (active) return;
    active = true;
    notify();
    while (queue.length > 0) {
      const { blob, idx } = queue.shift();
      notify();
      try {
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("idx", String(idx));
        fd.append("file", new File([blob], `chunk-${idx}.webm`, { type: blob.type || "video/webm" }));
        // Extended retry with exponential backoff — targets low-signal
        // shipboard Wi-Fi where a single retry wasn't enough (reported:
        // "students with low network couldn't take the test"). Up to
        // 5 attempts with 800ms → 12.8s backoff; the queue survives
        // transient disconnects in-memory for the duration of the test.
        let lastErr = null;
        for (let i = 1; i <= 5; i++) {
          try {
            await api.post(`/student/attempt/${attemptId}/recording-chunk`, fd, { timeout: 45_000 });
            lastErr = null;
            break;
          } catch (e) {
            lastErr = e;
            if (i < 5) await new Promise((r) => setTimeout(r, 800 * Math.pow(2, i - 1)));
          }
        }
        if (lastErr) console.warn(`[chunk-upload] ${kind} chunk ${idx} failed after 5 retries`, lastErr);
      } catch (e) {
        console.warn(`[chunk-upload] ${kind} chunk dispatch error`, e);
      }
    }
    active = false;
    notify();
  };

  return {
    push(blob) {
      queue.push({ blob, idx: nextIdx++ });
      notify();
      // Fire a drain but don't await — MediaRecorder callback must return fast.
      flush();
    },
    pendingCount() { return queue.length + (active ? 1 : 0); },
    async drain() {
      // Wait until queue is empty AND no in-flight upload.
      while (queue.length > 0 || active) {
        await new Promise((r) => setTimeout(r, 150));
      }
    },
    get pending() { return queue.length; },
  };
}

// Voice Activity Detector using Web Audio API.
export class VAD {
  constructor(audioStream, { threshold = 0.022, onActive } = {}) {
    this.stream = audioStream;
    this.threshold = threshold;
    this.onActive = onActive;
    this.ctx = null;
    this.src = null;
    this.analyser = null;
    this.raf = null;
    this.level = 0;
    this.active = false;
  }
  start() {
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.src = this.ctx.createMediaStreamSource(this.stream);
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 1024;
    this.src.connect(this.analyser);
    const data = new Uint8Array(this.analyser.fftSize);
    const loop = () => {
      this.analyser.getByteTimeDomainData(data);
      let sumSq = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sumSq += v * v;
      }
      const rms = Math.sqrt(sumSq / data.length);
      this.level = rms;
      const wasActive = this.active;
      this.active = rms > this.threshold;
      if (this.active && !wasActive && this.onActive) this.onActive();
      this.raf = requestAnimationFrame(loop);
    };
    loop();
  }
  stop() {
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    try { this.src && this.src.disconnect(); } catch (e) {}
    this.src = null;
    this.analyser = null;
    // AudioContext.close() returns a Promise and throws InvalidStateError if already closed.
    // Guard against repeated stop() calls (VAD is torn down from multiple call sites).
    if (this.ctx) {
      const ctx = this.ctx;
      this.ctx = null;
      if (ctx.state !== "closed") {
        try {
          const p = ctx.close();
          if (p && typeof p.catch === "function") p.catch(() => {});
        } catch (e) {}
      }
    }
    this.active = false;
  }
}

export async function blobToFormFile(blob, name) {
  return new File([blob], name, { type: blob.type });
}

/**
 * Grab a single still frame (JPEG Blob) from a live MediaStream — used for
 * the periodic AI screen scan. Downscales to maxWidth to keep the payload
 * small. Resolves null on failure (best-effort; never throws to the caller).
 */
export async function captureFrameFromStream(stream, maxWidth = 1280) {
  return new Promise((resolve) => {
    try {
      const track = stream && stream.getVideoTracks && stream.getVideoTracks()[0];
      if (!track) return resolve(null);
      const video = document.createElement("video");
      video.muted = true;
      video.playsInline = true;
      video.srcObject = stream;
      let settled = false;
      const grab = () => {
        if (settled) return;
        settled = true;
        try {
          const vw = video.videoWidth || 1280;
          const vh = video.videoHeight || 720;
          const scale = Math.min(1, maxWidth / vw);
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(vw * scale);
          canvas.height = Math.round(vh * scale);
          canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) => {
              try { video.pause(); video.srcObject = null; } catch (e) {}
              resolve(blob || null);
            },
            "image/jpeg",
            0.6,
          );
        } catch (e) {
          try { video.pause(); video.srcObject = null; } catch (_e) {}
          resolve(null);
        }
      };
      video.onloadedmetadata = () => {
        video.play().then(() => setTimeout(grab, 250)).catch(() => setTimeout(grab, 250));
      };
      // Hard fallback so we never hang the scan interval.
      setTimeout(grab, 2000);
    } catch (e) {
      resolve(null);
    }
  });
}

export function stopAllTracks(stream) {
  if (!stream) return;
  try { stream.getTracks().forEach((t) => t.stop()); } catch (e) {}
}

/**
 * Creates a watermarked video stream from the screen-share stream.
 * Draws the screen content into a canvas at ~10fps and overlays a
 * subtle diagonal repeating watermark with the student's name, ID and IP.
 * Returns { stream, stop } — call stop() to cleanly tear down the render loop.
 */
export function createWatermarkedScreenStream(screenStream, { name, id, ip } = {}) {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.srcObject = screenStream;

  const canvas = document.createElement("canvas");
  // Initial size; updated once video metadata loads
  canvas.width = 1280;
  canvas.height = 720;
  const ctx = canvas.getContext("2d");

  let rafHandle = null;
  let running = true;

  video.onloadedmetadata = () => {
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    video.play().catch(() => {});
  };

  const wmLabel = [name, id, ip].filter(Boolean).join(" · ");
  const fullLabel = `HIMT · ${wmLabel} · ${new Date().toLocaleDateString()}`;

  const draw = () => {
    if (!running) return;
    try {
      if (video.readyState >= 2 && video.videoWidth) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        // Diagonal tiled watermark
        ctx.save();
        ctx.globalAlpha = 0.11;
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#000000";
        ctx.lineWidth = 1;
        const fontSize = Math.max(14, Math.round(canvas.width / 90));
        ctx.font = `600 ${fontSize}px ui-monospace, SFMono-Regular, Menlo, monospace`;
        ctx.textBaseline = "middle";
        const stepX = Math.max(260, canvas.width / 4);
        const stepY = Math.max(140, canvas.height / 5);
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(-Math.PI / 7);
        ctx.translate(-canvas.width, -canvas.height);
        for (let y = 0; y < canvas.height * 2; y += stepY) {
          for (let x = 0; x < canvas.width * 2; x += stepX) {
            ctx.strokeText(fullLabel, x, y);
            ctx.fillText(fullLabel, x, y);
          }
        }
        ctx.restore();

        // Opaque corner badge for tamper-evident identification
        ctx.save();
        ctx.globalAlpha = 0.85;
        ctx.fillStyle = "rgba(10,37,64,0.85)";
        const badgeFont = Math.max(11, Math.round(canvas.width / 130));
        ctx.font = `700 ${badgeFont}px ui-monospace, SFMono-Regular, Menlo, monospace`;
        const txt = ` HIMT · ${wmLabel} · ${new Date().toLocaleTimeString()} `;
        const metrics = ctx.measureText(txt);
        const padY = Math.round(badgeFont * 1.8);
        ctx.fillRect(8, canvas.height - padY - 8, metrics.width + 12, padY);
        ctx.fillStyle = "#ffffff";
        ctx.fillText(txt, 14, canvas.height - padY / 2 - 8);
        ctx.restore();
      }
    } catch (e) { /* drawImage before ready */ }
    rafHandle = requestAnimationFrame(draw);
  };
  draw();

  const stream = canvas.captureStream(10);
  // Preserve any audio tracks from the original screen stream (if user opted in)
  screenStream.getAudioTracks().forEach((t) => stream.addTrack(t));

  return {
    stream,
    stop: () => {
      running = false;
      if (rafHandle) cancelAnimationFrame(rafHandle);
      try { video.pause(); } catch (e) {}
      try { video.srcObject = null; } catch (e) {}
    },
  };
}
